import { useCallback, useEffect, useState } from 'react';
import { Title } from 'react-admin';

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import {
    downloadSpeechCalibrationExport,
    getSpeechCalibrationReadiness,
    type SpeechCalibrationReadiness,
} from '../admin/adminSpeechCalibrationApi';
import { todayUtc } from './speechCalibrationConstants';
import { MetricCard, StatusChip } from './speechCalibrationUi';

function dateDaysAgo(days: number) {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - days);
    return date.toISOString().slice(0, 10);
}

function formatMetric(value: number | null, digits = 2) {
    return value == null ? 'Pending' : new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(value);
}

function formatPercent(value: number | null) {
    if (value == null) return 'Pending';
    const percent = value <= 1 ? value * 100 : value;
    return `${formatMetric(percent, 1)}%`;
}

export function SpeechCalibrationReadinessPage() {
    const [examName, setExamName] = useState('');
    const [examTaskType, setExamTaskType] = useState('');
    const [modelRevision, setModelRevision] = useState('');
    const [fromUtc, setFromUtc] = useState(dateDaysAgo(30));
    const [toUtc, setToUtc] = useState(todayUtc());
    const [data, setData] = useState<SpeechCalibrationReadiness | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            setData(await getSpeechCalibrationReadiness({ examName, examTaskType, modelRevision }));
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load calibration readiness');
        } finally {
            setIsLoading(false);
        }
    }, [examName, examTaskType, modelRevision]);

    useEffect(() => {
        void load();
    }, [load]);

    const exportLabels = async () => {
        setIsExporting(true);
        setError(null);
        try {
            await downloadSpeechCalibrationExport({
                fromUtc: `${fromUtc}T00:00:00.000Z`,
                toUtc: `${toUtc}T23:59:59.999Z`,
                examName,
                examTaskType,
            });
        } catch (exportError) {
            setError(exportError instanceof Error ? exportError.message : 'Unable to export calibration labels');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1.25, md: 2.5 }, minWidth: 0 }}>
            <Title title="Speech Calibration Readiness" />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 900 }}>Calibration readiness</Typography>
                    <Typography sx={{ mt: 0.4, color: '#64748b' }}>
                        Review coverage, agreement, model error and subgroup safety before exporting adjudicated labels.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    disabled={isLoading}
                    onClick={() => void load()}
                    sx={{ alignSelf: { xs: 'stretch', md: 'flex-start' } }}
                >
                    Refresh
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1.25,
                    }}
                >
                    <FormControl fullWidth>
                        <InputLabel id="readiness-exam-label">Exam</InputLabel>
                        <Select
                            labelId="readiness-exam-label"
                            label="Exam"
                            value={examName}
                            onChange={(event) => setExamName(String(event.target.value))}
                        >
                            <MenuItem value="">All exams</MenuItem>
                            <MenuItem value="Academic">Academic</MenuItem>
                            <MenuItem value="Core">Core</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Speaking task type"
                        value={examTaskType}
                        onChange={(event) => setExamTaskType(event.target.value)}
                        placeholder="All speaking tasks"
                    />
                    <TextField
                        label="Model revision"
                        value={modelRevision}
                        onChange={(event) => setModelRevision(event.target.value)}
                        placeholder="Latest"
                    />
                </Box>
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 850 }}>Readiness snapshot</Typography>
                {data ? <StatusChip status={data.status} /> : null}
                {data?.releaseId ? <Chip size="small" variant="outlined" label={`Artifact ${data.releaseId}`} /> : null}
            </Stack>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1.25,
                    mb: 2,
                }}
            >
                <MetricCard label="Unique recordings" value={data?.uniqueRecordings ?? 0} />
                <MetricCard label="Independent coverage" value={formatPercent(data?.independentRatingCoverage ?? null)} />
                <MetricCard label="Agreement" value={formatMetric(data?.agreement ?? null)} />
                <MetricCard label="Mean absolute error" value={formatMetric(data?.meanAbsoluteError ?? null)} />
                <MetricCard label="P90 absolute error" value={formatMetric(data?.p90AbsoluteError ?? null)} />
                <MetricCard label="False correction rate" value={formatPercent(data?.falseCorrectionRate ?? null)} />
                <MetricCard label="Accent false alerts" value={formatPercent(data?.validAccentFalseAlertRate ?? null)} />
                <MetricCard
                    label="Mandatory gates"
                    value={`${data?.gates.filter((gate) => gate.mandatory && gate.passed === true).length ?? 0}/${data?.gates.filter((gate) => gate.mandatory).length ?? 0}`}
                />
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                }}
            >
                <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontWeight: 850 }}>Readiness gates</Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                            Nullable results remain pending until the minimum sample requirement is met.
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 850 }}>Gate</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Target</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Current</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Result</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.gates.map((gate) => (
                                    <TableRow key={gate.code}>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 750 }}>{gate.label}</Typography>
                                            {!gate.mandatory ? <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>Advisory</Typography> : null}
                                        </TableCell>
                                        <TableCell>{gate.target}</TableCell>
                                        <TableCell>{gate.current ?? 'Pending'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={gate.passed == null ? 'default' : gate.passed ? 'success' : 'error'}
                                                label={gate.passed == null ? 'Pending' : gate.passed ? 'Passed' : 'Not passed'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {!isLoading && (data?.gates.length ?? 0) === 0 ? (
                        <Typography sx={{ py: 4, textAlign: 'center', color: '#64748b' }}>No readiness gates available.</Typography>
                    ) : null}
                </Paper>

                <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontWeight: 850 }}>Language subgroups</Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                            Missing user language is grouped as unknown and never blocks review.
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 850 }}>Language</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 850 }}>Samples</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 850 }}>Error</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.subgroups.map((group) => (
                                    <TableRow key={group.key}>
                                        <TableCell>{group.key || 'unknown'}</TableCell>
                                        <TableCell align="right">{group.sampleCount}</TableCell>
                                        <TableCell align="right">{formatMetric(group.error)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {!isLoading && (data?.subgroups.length ?? 0) === 0 ? (
                        <Typography sx={{ py: 4, textAlign: 'center', color: '#64748b' }}>No subgroup metrics available.</Typography>
                    ) : null}
                </Paper>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2.5 }}>
                <Typography sx={{ fontWeight: 850 }}>Export adjudicated labels</Typography>
                <Typography sx={{ mt: 0.25, mb: 1.5, color: '#64748b', fontSize: '0.82rem' }}>
                    V1 exports reviewed labels only. It does not fit, activate or release a calibration model.
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                    <TextField
                        label="From (UTC)"
                        type="date"
                        value={fromUtc}
                        onChange={(event) => setFromUtc(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                    <TextField
                        label="To (UTC)"
                        type="date"
                        value={toUtc}
                        onChange={(event) => setToUtc(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        startIcon={isExporting ? <CircularProgress size={17} color="inherit" /> : <DownloadIcon />}
                        disabled={isExporting || !fromUtc || !toUtc || fromUtc > toUtc}
                        onClick={() => void exportLabels()}
                        sx={{ minWidth: 180 }}
                    >
                        Export CSV
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
